import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import useApiManager from '../../utils/Api/apiManager';

const LeadsAutoComplete = ({ setLeadId, leadId, shouldFetchSingleLead }) => {
    const [leadOptions, setLeadOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [inputValue, setInputValue] = useState('');
    const pageSize = 6;
    const { post, get } = useApiManager();

    const fetchAndSetData = async (pageNum, inputText) => {
        try {
            setLoading(true);

            const payload = {
                pageNumber: pageNum,
                pageSize,
                filters: [
                    {
                        fieldName: 'firstName', // Filter field
                        filterOperator: 2, // Example: "contains" operator
                        fieldValue: inputText || '', // Use inputValue for filtering
                        fieldType: 1, // Example: string field type
                    },
                ],
                sortField: '', // Optional: Add sort field if needed
                sortDescending: false, // Optional: Set sorting direction
            };

            const response = await post('/leads/paged', payload);

            const options = response.items.map(lead => ({
                label: `${lead.firstName} ${lead.lastName}`,
                value: lead.id,
            }));

            setLeadOptions(prev =>
                pageNum === 1 ? options : [...prev, ...options] // Reset or append options based on page
            );
            setTotalCount(response.totalCount);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when the component mounts or input value changes
    useEffect(() => {
        if (inputValue.length >= 2) {
            fetchAndSetData(1, inputValue); // Fetch with updated input value
        } else if (inputValue === '') {
            fetchAndSetData(1, ''); // Clear filter when input is cleared
        }
    }, [inputValue]);

    // Fetch single lead by ID
    useEffect(() => {
        if (leadId > 0 && shouldFetchSingleLead) {
            const fetchleadById = async () => {
                try {
                    setLoading(true);
                    const response = await get(`/leads/${leadId}`);
                    const leadOption = {
                        label: `${response.firstName} ${response.lastName}`,
                        value: response.id,
                    };

                    // Add the fetched lead to options
                    setLeadOptions((prev) => {
                        // Check if the lead already exists in the options
                        const leadExists = prev.some(option => option.value === leadOption.value);

                        // If it doesn't exist, add it; otherwise, return the current options
                        if (!leadExists) {
                            return [leadOption, ...prev];
                        }
                        return prev;
                    });


                    // Set the selected option
                    setSelectedOption(leadOption);
                } catch (error) {
                    console.error('Error fetching lead by ID:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchleadById();
        }
    }, [leadId, shouldFetchSingleLead]);

    const handleInputChange = (newValue) => {
        setInputValue(newValue);
        setPageNumber(1);
    };

    const handleScrollToBottom = () => {
        const totalFetched = leadOptions.length;
        if (totalFetched < totalCount && !loading) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            fetchAndSetData(nextPage, inputValue);
        }
    };

    const customStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            height: '56px',
            padding: '0px 8px',
            borderRadius: '4px',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 10,
            backgroundColor: '#fff',
            borderRadius: '4px',
        }),
    };

    return (
        <div style={{ width: '100%' }}>
            <Select
                isLoading={loading}
                isClearable
                options={leadOptions}
                value={selectedOption} // Use the explicitly managed selected option
                onChange={(selectedOption) => {
                    setLeadId(selectedOption ? selectedOption.value : null);
                    setSelectedOption(selectedOption); // Update the selected option
                }}
                onInputChange={handleInputChange}
                placeholder="Select Lead"
                styles={customStyles}
                maxMenuHeight={200}
                onMenuScrollToBottom={handleScrollToBottom} // Load more data on scroll
                noOptionsMessage={() => (loading ? 'Loading...' : 'No options available')}
            />
        </div>
    );
};
export default LeadsAutoComplete;